/*
  File:        BookingsPage.tsx
  Path:        /Users/larrymclean/CascadeProjects/aquorix-frontend/src/features/dashboard/pages/BookingsPage.tsx
  Description:
    Phase 8.7 (P0): Phone-first Bookings Queue page.
    - Calls GET /api/v1/dashboard/bookings via apiFetch (injects Bearer token)
    - Renders phone-first booking cards with deterministic badges + filter chips
    - No backend changes. No new endpoints.
  
  Queue Order Doctrine (NO-DRIFT):
    - For now, UI applies a deterministic client-side sort:
      session_date ASC → start_time ASC → created_at DESC
    - This is a deliberate, documented “queue order law” for Phase 8.7.
    - If backend order becomes authoritative later, we will remove client sorting only
      after confirming the backend ordering contract in writing.

  Author: AQUORIX Team
  Created: 2026-02-27
  Version: 0.4.0

  Change Log (append-only):
    - 2026-02-27 - v0.1.0:
      - Initial scaffold page (placeholder UI)
    - 2026-02-27 - v0.2.0:
      - Wire GET /api/v1/dashboard/bookings and render raw payload for capture
    - 2026-02-27 - v0.3.0:
      - Phone-first queue UI: cards + status badges + filter chips + safe sort
    - 2026-06-19 - v0.4.0:
      - Add lifecycle triage band.
      - Split list into Needs Attention and On Track groups.
      - Align Bookings list with P10 operational/financial truth workflow.
*/

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../utils/api";
import BookingCard, { type BookingCardData } from "../components/BookingCard";

type ApiWeek = { start: string; end: string };

type ApiBooking = BookingCardData & {
  requires_manual_review_derived: boolean;
  manual_review_required?: boolean;
  manual_review_reason?: string | null;

  hold_expires_at?: string | null;
  paid_at?: string | null;
  updated_at?: string | null;
};

type ApiResponse = {
  ok: boolean;
  status: string;
  operator_id: string;
  week: ApiWeek;
  bookings: ApiBooking[];
};

type LoadState =
  | { status: "idle" | "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: ApiResponse };

type FilterKey = "all" | "needs_review" | "awaiting_payment" | "confirmed" | "paid" | "cancelled";

function getLocalYmd(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function getLocalIsoDow(d: Date) {
  // JS: 0=Sun..6=Sat -> ISO: 1=Mon..7=Sun
  const js = d.getDay();
  return js === 0 ? 7 : js;
}

function getLocalWeekStartYmd(d: Date) {
  const isoDow = getLocalIsoDow(d); // 1..7
  const monday = new Date(d);
  monday.setDate(d.getDate() - (isoDow - 1));
  return getLocalYmd(monday);
}

function isNeedsAttentionBooking(b: ApiBooking): boolean {
  const ui = String(b.ui_status || "").toLowerCase();
  const bookingStatus = String(b.booking_status || "").toLowerCase();
  const paymentStatus = String(b.payment_status || "").toLowerCase();
  const refundStatus = String((b as any).refund_status || "").toLowerCase();

  if (ui === "paid_manual_review") return true;
  if (bookingStatus === "cancelled" && paymentStatus === "paid" && refundStatus !== "refunded") return true;
  if (bookingStatus === "confirmed" && paymentStatus === "unpaid") return true;

  return false;
}

function getMoneyMinor(b: ApiBooking): number {
  const direct = Number(b.payment_amount_minor);
  if (Number.isFinite(direct)) return direct;

  const amount = Number(b.payment_amount);
  if (Number.isFinite(amount)) return Math.round(amount * 1000);

  return 0;
}

function formatMinorJod(minor: number): string {
  return `JOD ${(Number(minor || 0) / 1000).toFixed(2)}`;
}

function matchesFilter(b: ApiBooking, filter: FilterKey): boolean {
  const ui = (b.ui_status || "").toLowerCase();
  const pay = (b.payment_status || "").toLowerCase();
  const book = (b.booking_status || "").toLowerCase();

  if (filter === "all") return true;
  if (filter === "needs_review") return ui === "paid_manual_review";
  if (filter === "awaiting_payment") return book === "pending" && pay === "unpaid";
  if (filter === "confirmed") return book === "confirmed";
  if (filter === "paid") return pay === "paid";
  if (filter === "cancelled") return ui === "cancelled" || book === "cancelled";
  return true;
}

function addDaysToYmd(ymd: string, days: number) {
  const [yyyy, mm, dd] = ymd.split("-").map((n) => parseInt(n, 10));
  const d = new Date(yyyy, mm - 1, dd);
  d.setDate(d.getDate() + days);
  return getLocalYmd(d);
}

export default function BookingsPage() {
  const [state, setState] = useState<LoadState>({ status: "idle" });
  const [filter, setFilter] = useState<FilterKey>("all");
  const [weekStart, setWeekStart] = useState<string>(() => getLocalWeekStartYmd(new Date()));
  const nav = useNavigate();

  function handlePreviousWeek() {
    setWeekStart((prev) => addDaysToYmd(prev, -7));
  }

  function handleNextWeek() {
    setWeekStart((prev) => addDaysToYmd(prev, 7));
  }

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setState({ status: "loading" });

      try {
        const res = await apiFetch(`/api/v1/dashboard/bookings?week_start=${encodeURIComponent(weekStart)}`, { method: "GET" });
        const json = (await res.json().catch(() => ({}))) as any;

        if (!res.ok || json?.ok === false) {
          const msg = json?.message || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        if (isMounted) setState({ status: "ok", data: json as ApiResponse });
      } catch (err: any) {
        if (isMounted) setState({ status: "error", message: err?.message || "Load failed" });
      }
    }

    load();
    return () => {
      isMounted = false;
    };
    }, [weekStart]);

  const bookings = useMemo(() => {
    if (state.status !== "ok") return [];
    const raw = state.data.bookings || [];
    const filtered = raw.filter((b) => matchesFilter(b, filter));
    return filtered; // preserve backend order (pending-first cockpit truth)
  }, [state, filter]);

  const counts = useMemo(() => {
    if (state.status !== "ok") {
      return { all: 0, needs_review: 0, awaiting_payment: 0, confirmed: 0, paid: 0, cancelled: 0 };
    }
    const raw = state.data.bookings || [];
    const c = {
      all: raw.length,
      needs_review: raw.filter((b) => matchesFilter(b, "needs_review")).length,
      awaiting_payment: raw.filter((b) => matchesFilter(b, "awaiting_payment")).length,
      confirmed: raw.filter((b) => matchesFilter(b, "confirmed")).length,
      paid: raw.filter((b) => matchesFilter(b, "paid")).length,
      cancelled: raw.filter((b) => matchesFilter(b, "cancelled")).length,
    };
    return c;
  }, [state]);

    const triage = useMemo(() => {
    if (state.status !== "ok") {
      return {
        needsAttention: 0,
        paidManualReview: 0,
        fundsHeldMinor: 0,
        confirmedUnpaidMinor: 0,
      };
    }

    const raw = state.data.bookings || [];

    return {
      needsAttention: raw.filter(isNeedsAttentionBooking).length,
      paidManualReview: raw.filter((b) => {
        const ui = String(b.ui_status || "").toLowerCase();
        return ui === "paid_manual_review";
      }).length,
      fundsHeldMinor: raw
        .filter((b) => {
          const bookingStatus = String(b.booking_status || "").toLowerCase();
          const paymentStatus = String(b.payment_status || "").toLowerCase();
          const refundStatus = String((b as any).refund_status || "").toLowerCase();
          return bookingStatus === "cancelled" && paymentStatus === "paid" && refundStatus !== "refunded";
        })
        .reduce((sum, b) => sum + getMoneyMinor(b), 0),
      confirmedUnpaidMinor: raw
        .filter((b) => {
          const bookingStatus = String(b.booking_status || "").toLowerCase();
          const paymentStatus = String(b.payment_status || "").toLowerCase();
          return bookingStatus === "confirmed" && paymentStatus === "unpaid";
        })
        .reduce((sum, b) => sum + getMoneyMinor(b), 0),
    };
  }, [state]);

  const needsAttentionBookings = useMemo(() => {
    return bookings.filter(isNeedsAttentionBooking);
  }, [bookings]);

  const onTrackBookings = useMemo(() => {
    return bookings.filter((b) => !isNeedsAttentionBooking(b));
  }, [bookings]);

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div style={styles.crumb}>Operations › <strong>Bookings</strong></div>
        <h1 style={styles.h1}>Bookings</h1>

        {state.status === "ok" ? (
          <div style={styles.pageSub}>
            Your booking queue for the week — what needs your attention, first. Operator #{state.data.operator_id} · Week {state.data.week?.start} → {state.data.week?.end}
          </div>
        ) : (
          <div style={styles.pageSub}>
            Your booking queue for the week — what needs your attention, first.
          </div>
        )}
      </div>

      {state.status === "loading" || state.status === "idle" ? <div style={styles.card}>Loading bookings…</div> : null}

      {state.status === "error" ? (
        <div style={styles.bannerError}>
          <strong>Bookings load failed.</strong>
          <div style={{ marginTop: 6 }}>{state.message}</div>
        </div>
      ) : null}

      {state.status === "ok" ? (
        <>
          {/* Week navigation */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 12,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={handlePreviousWeek}
              style={styles.weekChip}
            >
              ‹ Previous week
            </button>

            <div style={{ fontSize: 13, fontWeight: 800 }}>
              Week {state.data.week?.start} → {state.data.week?.end}
            </div>

            <button
              type="button"
              onClick={handleNextWeek}
              style={styles.weekChip}
            >
              Next week ›
            </button>
          </div>

          {/* Lifecycle triage band */}
          <div style={styles.triageBand}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setFilter("needs_review")}
              style={{ ...styles.triageCardReview, cursor: "pointer" }}
            >
              <div style={styles.triageValue}>{triage.paidManualReview}</div>
              <div style={styles.triageLabel}>Needs your review</div>
              <div style={styles.triageSub}>Paid, seat unconfirmed</div>
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => setFilter("awaiting_payment")}
              style={{ ...styles.triageCardHold, cursor: "pointer" }}
            >
              <div style={styles.triageValue}>0</div>
              <div style={styles.triageLabel}>Holds expiring soon</div>
              <div style={styles.triageSub}>Awaiting payment, &lt; 24h</div>
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => setFilter("confirmed")}
              style={{ ...styles.triageCardBalance, cursor: "pointer" }}
            >
              <div style={styles.triageValueSmall}>{formatMinorJod(triage.confirmedUnpaidMinor)}</div>
              <div style={styles.triageLabel}>Confirmed, unpaid</div>
              <div style={styles.triageSub}>Money to collect</div>
            </div>
          </div>

          <div style={styles.triageNote}>
            Review counts span all weeks — a seat flagged three weeks ago still surfaces here.
          </div>

          {/* Filter chips */}
          <div style={styles.chipsRow}>
            <Chip label={`All ${counts.all}`} active={filter === "all"} onClick={() => setFilter("all")} />
            <Chip
              label={`Needs review ${counts.needs_review}`}
              active={filter === "needs_review"}
              onClick={() => setFilter("needs_review")}
            />
            <Chip
              label={`Awaiting payment ${counts.awaiting_payment}`}
              active={filter === "awaiting_payment"}
              onClick={() => setFilter("awaiting_payment")}
            />
            <Chip
              label={`Confirmed ${counts.confirmed}`}
              active={filter === "confirmed"}
              onClick={() => setFilter("confirmed")}
            />
            <Chip label={`Paid ${counts.paid}`} active={filter === "paid"} onClick={() => setFilter("paid")} />
            <Chip
              label={`Cancelled ${counts.cancelled}`}
              active={filter === "cancelled"}
              onClick={() => setFilter("cancelled")}
            />
          </div>

          {/* Cards */}
          <div style={{ height: 10 }} />

          {bookings.length === 0 ? (
            <div style={styles.card}>No bookings match this filter.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {needsAttentionBookings.length > 0 ? (
                <section style={styles.queueSection}>
                  <div style={styles.queueHeader}>
                    <div>
                      <div style={styles.queueEyebrow}>⚑ Needs your attention</div>
                      <h2 style={styles.queueTitle}>Needs your attention</h2>
                    </div>
                    <div style={styles.queueCount}>{needsAttentionBookings.length}</div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {needsAttentionBookings.map((b) => (
                      <BookingCard
                        key={b.booking_id}
                        booking={b}
                        onOpen={() => {
                          const weekStart = getLocalWeekStartYmd(new Date());
                          nav(`/dashboard/bookings/${b.booking_id}`, {
                            state: { booking: b, weekStart },
                          });
                        }}
                      />
                    ))}
                  </div>
                </section>
              ) : null}

              {onTrackBookings.length > 0 ? (
                <section style={styles.queueSection}>
                  <div style={styles.queueHeader}>
                    <div>
                      <div style={styles.queueEyebrow}>On Track</div>
                      <h2 style={styles.queueTitle}>On track</h2>
                    </div>
                    <div style={styles.queueCount}>{onTrackBookings.length}</div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {onTrackBookings.map((b) => (
                      <BookingCard
                        key={b.booking_id}
                        booking={b}
                        onOpen={() => {
                          const weekStart = getLocalWeekStartYmd(new Date());
                          nav(`/dashboard/bookings/${b.booking_id}`, {
                            state: { booking: b, weekStart },
                          });
                        }}
                      />
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function Chip(props: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={props.onClick}
      style={{
        ...styles.chip,
        ...(props.active ? styles.chipActive : null),
      }}
    >
      {props.label}
    </button>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: "26px 30px 90px", maxWidth: 980, background: "var(--page)" },
  h1: { fontSize: 26, fontWeight: 800, margin: "0 0 2px", color: "var(--deep-aqua)" },

  pageHeader: {
    marginBottom: 18,
  },

  crumb: {
    fontSize: 12,
    color: "var(--slate)",
    marginBottom: 24,
  },

  pageSub: {
    color: "var(--slate)",
    fontSize: 13.5,
    lineHeight: 1.5,
  },

  card: {
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 14,
    padding: 14,
    background: "var(--surface)",
  },

  bannerError: {
    border: "1px solid rgba(220, 38, 38, 0.35)",
    background: "rgba(220, 38, 38, 0.08)",
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },

  triageBand: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 6,
  },

  triageCardReview: {
    border: "1px solid var(--line)",
    borderLeft: "4px solid var(--rare)",
    background: "var(--surface)",
    borderRadius: 11,
    padding: "14px 16px",
    minHeight: 86,
  },

  triageCardHold: {
    border: "1px solid var(--line)",
    borderLeft: "4px solid var(--gold)",
    background: "var(--surface)",
    borderRadius: 11,
    padding: "14px 16px",
    minHeight: 86,
  },

  triageCardBalance: {
    border: "1px solid var(--line)",
    borderLeft: "4px solid var(--go)",
    background: "var(--surface)",
    borderRadius: 11,
    padding: "14px 16px",
    minHeight: 86,
  },

  triageButton: {
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
    appearance: "none",
    WebkitAppearance: "none",
    outline: "none",
    color: "inherit",
    boxSizing: "border-box",
  },

  triageLabel: {
    fontSize: 10,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: "var(--slate)",
  },

  triageValue: {
    fontSize: 28,
    fontWeight: 950,
    color: "var(--ink)",
    marginTop: 8,
    lineHeight: 1,
  },

  triageValueSmall: {
    fontSize: 20,
    fontWeight: 950,
    color: "var(--ink)",
    marginTop: 10,
    lineHeight: 1.1,
  },

  triageNote: {
    fontSize: 11.5,
    color: "var(--slate)",
    margin: "10px 2px 18px",
  },

  queueSection: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  queueHeader: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    padding: "4px 2px 0",
  },

  queueEyebrow: {
    fontSize: 10,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: "0.16em",
    color: "var(--slate)",
  },

  queueTitle: {
    fontSize: 15,
    fontWeight: 950,
    color: "var(--ink)",
    margin: "3px 0 0",
  },

  queueCount: {
    minWidth: 34,
    height: 28,
    borderRadius: 3,
    border: "1px solid rgba(15, 47, 67, 0.14)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 950,
    color: "var(--ink)",
    background: "var(--surface)",
  },

  weekChip: {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid var(--line)",
    background: "var(--surface)",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: 12,
    color: "var(--deep-aqua)",
  },

  chip: {
    padding: "6px 14px",
    borderRadius: 3,
    border: "1px solid var(--line)",
    background: "var(--surface)",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: 12,
    color: "var(--slate)",
    marginRight: 8,
    marginBottom: 8,
  },

  chipActive: {
    background: "var(--deep-aqua)",
    border: "1px solid var(--deep-aqua)",
    color: "var(--surface)",
  },
};
